import { memo, useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { isEmpty, first, isNil, noop } from 'lodash';
// Import Types
import type { DropdownOption } from 'types';
// Import Store
import { useAppSelector } from 'store';
import { selectCurrencyRates } from 'store/slices/currencyRates';
import { selectPartnerCurrencies } from 'store/slices/partnerSettings';
// Import Services
import { customMoment, doesBonusHaveExpiration, formatNumber, getBonusSupportedCurrencies } from 'services/Helpers';
// Import Hooks
import { useMenuItem } from 'hooks/UseMenuItem';
// Import Components
import CJPartnerBonusId from './CJPartnerBonusId';
import BonusAmountsWrapper from './BonusAmountsWrapper';
import CurrencyAutoFillSection from 'components/common/CurrencyAutoFillSection';
import { ExpirationTypesDropdownData } from 'components/BonusList/configs';
// Import UI Components
import { BusyLoader, DatePickerInput, Dropdown, ExtendedInput } from '@geneui/components';
// Import Validation
import { CJBonusExpirationValidationSchema } from 'validators/schemas.validation';
// Import Constants
import {
    l,
    BonusTypesLabels,
    BonusTriggerTypeEnum,
    ExpirationTypes,
    MonthDayYearFormat,
    DateToISOStringFormat,
    ExpirationBonusModelTypes,
    ExpirationLabels,
} from 'constants/common';
// Import Enums
import { FormatNumberActionEnum } from 'enums';

const CJBonus = ({ defaultValue, getUpdate, option, updateDependency }) => {
    const { t } = useTranslation();

    const [savedDataForRestoreBonus, setSavedDataForRestoreBonus] = useState<{
        bonusAmountsList?: AnyObject[];
        bonusId?: string | number;
    }>({});
    const [bonusDetails, setBonusDetails] = useState([]);
    const [bonusType, setBonusType] = useState<number>();
    const defaultBonusId = defaultValue?.bonusId;
    const [bonusId, setBonusId] = useState(defaultBonusId);
    const [triggerType, setTriggerType] = useState(defaultValue?.triggerType);
    const [isLoadingBonusDetails, setIsLoadingBonusDetails] = useState(false);
    const [supportedCurrencies, setSupportedCurrencies] = useState<string[]>([]);
    const [bonusAmountsList, setBonusAmountsList] = useState([...(defaultValue?.amounts ?? [])]);
    const [selectedWallet, setSelectedWallet] = useState(null);
    const { convertedData: currenciesDropdownData, isLoading: isLoadingCurrencyData } = useMenuItem(104);
    const partnerCurrencies = useAppSelector(selectPartnerCurrencies);
    const totalCurrencyRates = useAppSelector(selectCurrencyRates);

    const ExpirationMinDate = customMoment().add(1, 'days').format(MonthDayYearFormat);
    const hasBonusExpirationDate = doesBonusHaveExpiration(bonusType);

    const formik = useFormik({
        initialValues: {
            [ExpirationLabels.Type]: '',
            [ExpirationLabels.Period]: 1,
            [ExpirationLabels.Date]: ExpirationMinDate,
            [ExpirationLabels.HasExpiration]: hasBonusExpirationDate,
        },
        onSubmit: noop,
        validationSchema: CJBonusExpirationValidationSchema(),
    });
    const { values, errors, setFieldValue, setValues } = formik;
    const { expirationType, expirationPeriod, expirationDate } = values;

    const expirationTypesData = ExpirationTypesDropdownData(t);

    const getExpirationDetails = useCallback(
        (type, date, period) => {
            if (!hasBonusExpirationDate) {
                return;
            }

            const expiration: { Date?: string; Period?: string } = {};

            if (type === ExpirationTypes.ExpirationDate) {
                const result = moment(date, MonthDayYearFormat).format(DateToISOStringFormat);
                expiration.Date = result;
            } else if (type === ExpirationTypes.ExpirationPeriod) {
                expiration.Period = `${period}.00:00:00`;
            }

            return {
                $type: ExpirationBonusModelTypes[type],
                ...expiration,
            };
        },
        [hasBonusExpirationDate],
    );

    const expirationTypeChangeHandler = (item) => {
        setFieldValue(ExpirationLabels.Type, item.value ?? '');

        const tmpAmountList =
            bonusDetails?.map(({ CurrencyCode, MaxAmount, MinAmount }) => {
                return { CurrencyCode, OriginalValue: MinAmount === MaxAmount ? MinAmount : null };
            }) || [];

        getUpdate({
            bonusId: bonusId,
            triggerType: triggerType,
            bonusType: bonusType,
            amounts: tmpAmountList,
            expiration: getExpirationDetails(item.value, expirationDate, expirationPeriod),
        });
    };

    const daysInputChangeHandler = (e) => {
        setFieldValue(ExpirationLabels.Period, +e.target.value);

        const tmpAmountList =
            bonusDetails?.map(({ CurrencyCode, MaxAmount, MinAmount }) => {
                return { CurrencyCode, OriginalValue: MinAmount === MaxAmount ? MinAmount : null };
            }) || [];

        getUpdate({
            bonusId: bonusId,
            triggerType: triggerType,
            bonusType: bonusType,
            amounts: tmpAmountList,
            expiration: getExpirationDetails(expirationType, expirationDate, e.target.value),
        });
    };

    const expirationDateChangeHandler = (data) => {
        if (isNil(data)) return;

        setFieldValue(ExpirationLabels.Date, data);

        const tmpAmountList =
            bonusDetails?.map(({ CurrencyCode, MaxAmount, MinAmount }) => {
                return { CurrencyCode, OriginalValue: MinAmount === MaxAmount ? MinAmount : null };
            }) || [];

        getUpdate({
            bonusId: bonusId,
            triggerType: triggerType,
            bonusType: bonusType,
            amounts: tmpAmountList,
            expiration: getExpirationDetails(expirationType, data, expirationPeriod),
        });
    };

    useEffect(() => {
        if (isNil(defaultValue.expiration)) {
            setValues({
                [ExpirationLabels.Type]: '',
                [ExpirationLabels.Period]: 1,
                [ExpirationLabels.Date]: ExpirationMinDate,
                [ExpirationLabels.HasExpiration]: hasBonusExpirationDate,
            });
        } else {
            const expirationType = defaultValue.expiration.$type;

            setFieldValue(ExpirationLabels.Type, ExpirationBonusModelTypes[expirationType]);
            setFieldValue(ExpirationLabels.HasExpiration, hasBonusExpirationDate);

            if (expirationType === ExpirationBonusModelTypes.expirationDate) {
                const expirationDate = moment(defaultValue.expiration.Date, DateToISOStringFormat).format(
                    MonthDayYearFormat,
                );

                setFieldValue(ExpirationLabels.Date, expirationDate);
            }

            if (expirationType === ExpirationBonusModelTypes.expirationPeriod) {
                const tmpTimeSpan = defaultValue.expiration.Period.split('.');
                const expirationPeriod = tmpTimeSpan[0];

                setFieldValue(ExpirationLabels.Period, +expirationPeriod);
            }
        }
    }, [ExpirationMinDate, defaultValue, hasBonusExpirationDate, setFieldValue, setValues]);

    useEffect(() => {
        if (isNil(defaultValue.wallet)) {
            setSelectedWallet(null);
        } else {
            const walletIsSupported = !!partnerCurrencies.find(
                (currency) => currency.CurrencyId === defaultValue.wallet && currency.IsWalletAutocreatable,
            );
            setSelectedWallet(walletIsSupported ? defaultValue.wallet : null); // TODO discuss the case when the selected wallet is no longer supported, now we just set primary wallet
        }
    }, [defaultValue, partnerCurrencies]);

    useEffect(() => {
        if (bonusDetails && (defaultBonusId !== bonusId || (defaultBonusId === bonusId && bonusId === ''))) {
            const tmpAmountList =
                bonusDetails?.map(({ CurrencyCode, MaxAmount, MinAmount }) => {
                    return { CurrencyCode, OriginalValue: MinAmount === MaxAmount ? MinAmount : null };
                }) || [];

            getUpdate({
                bonusId: bonusId,
                triggerType: triggerType,
                bonusType: bonusType,
                amounts: tmpAmountList,
                ...(hasBonusExpirationDate && {
                    expiration: getExpirationDetails(expirationType, expirationDate, expirationPeriod),
                }),
            });
            setBonusAmountsList(tmpAmountList);
            setSelectedWallet(null);
        }
    }, [
        bonusDetails,
        bonusId,
        defaultBonusId,
        expirationDate,
        expirationPeriod,
        expirationType,
        getExpirationDetails,
        hasBonusExpirationDate,
        getUpdate,
        triggerType,
        bonusType,
    ]);

    const supportedWallets = useMemo(() => {
        return partnerCurrencies.filter((el) => el.IsWalletAutocreatable);
    }, [partnerCurrencies]);

    const bonusAmountListChangeHandler = useCallback(
        (index, value) => {
            const tmpBonusAmountsList = [...bonusAmountsList];
            tmpBonusAmountsList[index] = value;
            setBonusAmountsList(tmpBonusAmountsList);

            getUpdate({
                bonusId: bonusId,
                triggerType: triggerType,
                bonusType: bonusType,
                amounts: tmpBonusAmountsList,
                wallet: selectedWallet,
                ...(hasBonusExpirationDate && {
                    expiration: getExpirationDetails(expirationType, expirationDate, expirationPeriod),
                }),
            });
        },
        [
            bonusAmountsList,
            bonusId,
            expirationDate,
            expirationPeriod,
            getExpirationDetails,
            hasBonusExpirationDate,
            getUpdate,
            expirationType,
            selectedWallet,
            triggerType,
            bonusType,
        ],
    );

    const restoreBonusAmountsList = useCallback(
        (newBonusDetails = []) => {
            const tmpAmountList =
                savedDataForRestoreBonus.bonusAmountsList?.filter((list) =>
                    newBonusDetails.some(({ CurrencyCode }) => CurrencyCode === list.CurrencyCode),
                ) || [];
            setBonusAmountsList(tmpAmountList);
            setSavedDataForRestoreBonus({});
        },
        [savedDataForRestoreBonus.bonusAmountsList],
    );

    const setPartnerBonusDetails = useCallback(
        (promise, shouldRestoreSavedDataWhenBonusDetailsReloaded) => {
            setIsLoadingBonusDetails(true);
            promise
                .then((data) => {
                    const newBonusDetails = data?.BonusDetails ?? [];
                    setBonusDetails(newBonusDetails);
                    setBonusType(data?.Type);
                    setSupportedCurrencies(getBonusSupportedCurrencies(data));

                    if (
                        shouldRestoreSavedDataWhenBonusDetailsReloaded &&
                        data.Id === savedDataForRestoreBonus.bonusId
                    ) {
                        restoreBonusAmountsList(newBonusDetails);
                    }
                })
                .catch(() => {
                    if (!isNil(bonusId) && bonusId !== savedDataForRestoreBonus.bonusId) {
                        setSavedDataForRestoreBonus({ bonusId, bonusAmountsList });
                    }
                    setBonusType(undefined);
                    setBonusDetails([]);
                    setSupportedCurrencies([]);
                    setBonusAmountsList([]);
                })
                .finally(() => {
                    setIsLoadingBonusDetails(false);
                });
        },
        [bonusAmountsList, bonusId, restoreBonusAmountsList, savedDataForRestoreBonus.bonusId],
    );

    const partnerBonusIdChangeHandler = useCallback(({ value, triggerType }) => {
        setBonusId(value);
        setTriggerType(triggerType);
    }, []);

    const changeWalletHandler = (item) => {
        setSelectedWallet(item.value);
        const secondaryCurrencyOriginalValue = first(
            bonusAmountsList?.filter(
                ({ CurrencyCode }) => CurrencyCode === item.value || bonusType === BonusTypesLabels.FreeSpin,
            ),
        )?.OriginalValue;

        const tmpBonusAmountsList =
            bonusDetails
                ?.filter(
                    ({ CurrencyCode }) =>
                        isNil(item.value) || CurrencyCode === item.value || bonusType === BonusTypesLabels.FreeSpin,
                )
                .map(({ CurrencyCode, MaxAmount, MinAmount }) => {
                    return {
                        CurrencyCode,
                        OriginalValue:
                            !isNil(item.value) &&
                            (CurrencyCode === item.value || bonusType === BonusTypesLabels.FreeSpin)
                                ? secondaryCurrencyOriginalValue
                                : MinAmount === MaxAmount
                                  ? MinAmount
                                  : null,
                    };
                }) || [];
        setBonusAmountsList([...tmpBonusAmountsList]);

        getUpdate({
            bonusId: bonusId,
            triggerType: triggerType,
            bonusType: bonusType,
            amounts: [...tmpBonusAmountsList],
            wallet: item.value,
            ...(hasBonusExpirationDate && {
                expiration: getExpirationDetails(expirationType, expirationDate, expirationPeriod),
            }),
        });
    };

    const onAutoFillClickHandler = (data) => {
        const { amount, selectedCurrency } = data;
        setBonusAmountsList((prev) => {
            return prev.map((element) => {
                const tmpValue = (totalCurrencyRates?.[element.CurrencyCode][selectedCurrency].Rate ?? 0) * amount;
                const roundedValue = formatNumber(tmpValue, {
                    type: FormatNumberActionEnum.CleanNumber,
                    minPrecision: 9,
                    maxPrecision: 9,
                    returnType: 'number',
                });
                element.OriginalValue = roundedValue;
                return element;
            });
        });
    };

    const secondaryCurrenciesDropdownData = useMemo(() => {
        if (isEmpty(partnerCurrencies)) {
            return [{ label: t(l.PrimaryWallet), value: null }];
        } else {
            const tmpResult: DropdownOption<Nullable<string>>[] = partnerCurrencies
                .filter((currency) => {
                    return currency.IsWalletAutocreatable;
                })
                .map((currency) => {
                    return {
                        label: currency.CurrencyId,
                        value: currency.CurrencyId,
                    };
                });
            tmpResult.unshift({ label: t(l.PrimaryWallet), value: null });
            return tmpResult;
        }
    }, [partnerCurrencies, t]);

    const isVisibleWalletSelection = useMemo(() => {
        const hasSupportedWallets = !isEmpty(supportedWallets);

        const isNotSingleWalletBonus = ![
            BonusTypesLabels.LoyaltyPoint,
            BonusTypesLabels.NFTBonus,
            BonusTypesLabels.LoyaltyBenefit,
        ].includes(bonusType ?? -1);

        const isAutoBonusTriggerType = !isNil(triggerType) && triggerType !== BonusTriggerTypeEnum.Manual;

        const isFreeSpin = bonusType === BonusTypesLabels.FreeSpin;

        const hasWalletWithSupportedCurrency = supportedWallets.some((supportedWallet) =>
            supportedCurrencies.includes(supportedWallet.CurrencyId),
        );

        return (
            bonusId &&
            hasSupportedWallets &&
            isNotSingleWalletBonus &&
            (isAutoBonusTriggerType || isFreeSpin || hasWalletWithSupportedCurrency)
        );
    }, [supportedWallets, bonusType, triggerType, bonusId, supportedCurrencies]);

    const getExpirationTypeComponent = () => {
        switch (expirationType) {
            case ExpirationTypes.ExpirationDate:
                return (
                    <DatePickerInput
                        value={expirationDate}
                        title={t(l.ExpirationDate)}
                        placeholder={t(l.SelectExpirationDate)}
                        onChange={expirationDateChangeHandler}
                        min={ExpirationMinDate}
                        isValid={!errors[ExpirationLabels.Date]}
                        errorText={t((errors[ExpirationLabels.Date] as Nilable<AnyObject>)?.label)}
                    />
                );
            case ExpirationTypes.ExpirationPeriod:
                return (
                    <ExtendedInput
                        type="number"
                        title={t(l.Name)}
                        label={t(l.Days)}
                        placeholder={t(l.Days)}
                        labelAppearance="swap"
                        onChange={daysInputChangeHandler}
                        value={String(expirationPeriod)}
                        min={1}
                        isValid={!errors[ExpirationLabels.Period]}
                        errorText={t((errors[ExpirationLabels.Period] as Nilable<AnyObject>)?.label)}
                    />
                );

            default:
                return <></>;
        }
    };

    return (
        <>
            <div className="cj-bonus-container">
                <CJPartnerBonusId
                    defaultValue={{ value: defaultBonusId, triggerType: defaultValue?.triggerType }}
                    getUpdate={partnerBonusIdChangeHandler}
                    option={option}
                    updateDependency={updateDependency}
                    setBonusDetails={setPartnerBonusDetails}
                    isWFBonus={true} // TODO: Remove this props after CJ service release from backend
                />
                {isVisibleWalletSelection && (
                    <Dropdown
                        label={l.Wallet}
                        labelAppearance="swap"
                        value={selectedWallet}
                        onChange={changeWalletHandler}
                        data={secondaryCurrenciesDropdownData}
                        hasSearch={true}
                        placeholder={l.Wallet}
                        className="wallet-dropdown"
                    />
                )}
                {hasBonusExpirationDate && (
                    <div className="cj-bonus-expiration-container">
                        <Dropdown
                            label={t(l.ExpirationType)}
                            placeholder={t(l.ExpirationType)}
                            labelAppearance="swap"
                            data={expirationTypesData}
                            value={expirationType}
                            onChange={expirationTypeChangeHandler}
                            isValid={!errors[ExpirationLabels.Type]}
                            errorText={t((errors[ExpirationLabels.Type] as Nilable<AnyObject>)?.label)}
                        />
                        {getExpirationTypeComponent()}
                    </div>
                )}
            </div>
            <BusyLoader spinnerSize="medium" isBusy={isLoadingBonusDetails}>
                {bonusAmountsList.length > 1 && (
                    <CurrencyAutoFillSection
                        isLoading={isLoadingCurrencyData}
                        dropdownData={currenciesDropdownData}
                        onOk={onAutoFillClickHandler}
                        alertMessage={t(l.CurrencyAutoFillInfoAlert)}
                    />
                )}
                <div className="crm-cj-edit-modal-bonus-cmp-fields">
                    <BonusAmountsWrapper
                        bonusDetails={bonusDetails?.filter(
                            ({ CurrencyCode }) =>
                                isNil(selectedWallet) ||
                                CurrencyCode === selectedWallet ||
                                bonusType === BonusTypesLabels.FreeSpin,
                        )}
                        bonusAmountListChangeHandler={bonusAmountListChangeHandler}
                        bonusAmountsList={bonusAmountsList}
                        isRequired={option?.Required}
                    />
                </div>
            </BusyLoader>
        </>
    );
};

CJBonus.propTypes = {
    defaultValue: PropTypes.object,
    getUpdate: PropTypes.func.isRequired,
    option: PropTypes.object.isRequired,
    optionsSharedState: PropTypes.object.isRequired,
    updateDependency: PropTypes.func.isRequired,
};

CJBonus.defaultProps = {
    defaultValue: { amounts: [], bonusId: '' },
};

export default memo(CJBonus);
